import type { HatRender } from "../../../shared/shop-catalog";
import { drawCustomGrid } from "./CustomArt";

interface HatAnim {
  propAngle: number;
  bobTime: number;
  flapTime: number;
}

const BACK_KINDS = new Set<HatRender["kind"]>([
  "wings",
  "cape",
  "aura",
  "aura_trail",
  "backpack",
  "quiver",
  "shield",
  "tail",
]);

export function drawHatBack(
  ctx: CanvasRenderingContext2D,
  r: number,
  hat: HatRender,
  anim: HatAnim,
): void {
  if (!BACK_KINDS.has(hat.kind)) return;
  drawHat(ctx, r, hat, anim);
}

export function drawHatFront(
  ctx: CanvasRenderingContext2D,
  r: number,
  hat: HatRender,
  anim: HatAnim,
): void {
  if (BACK_KINDS.has(hat.kind)) return;
  drawHat(ctx, r, hat, anim);
}

function drawHat(
  ctx: CanvasRenderingContext2D,
  r: number,
  hat: HatRender,
  anim: HatAnim,
): void {
  switch (hat.kind) {
    case "propeller":
      drawPropeller(ctx, r, hat, anim.propAngle);
      return;
    case "halo":
      drawHalo(ctx, r, hat.primary);
      return;
    case "horns":
      drawHorns(ctx, r, hat.primary);
      return;
    case "beret":
      drawBeret(ctx, r, hat.primary);
      return;
    case "hood":
      drawHood(ctx, r, hat.primary);
      return;
    case "visor":
      drawVisor(ctx, r, hat.primary);
      return;
    case "scarf":
      drawScarf(ctx, r, hat.primary);
      return;
    case "boots":
      drawBoots(ctx, r, hat.primary);
      return;
    case "gloves":
      drawGloves(ctx, r, hat.primary);
      return;
    case "mask":
      drawMask(ctx, r, hat.primary);
      return;
    case "eyepatch":
      drawEyepatch(ctx, r, hat.primary);
      return;
    case "aura":
      drawAura(ctx, r, hat.primary, anim.bobTime);
      return;
    case "aura_trail":
      drawAuraTrail(ctx, r, hat.primary, anim.bobTime);
      return;
    case "wings":
      drawWings(ctx, r, hat.primary, anim.bobTime, anim.flapTime);
      return;
    case "crown":
      drawCrown(ctx, r, hat.primary, hat.secondary);
      return;
    case "fedora":
      drawFedora(ctx, r, hat.primary, hat.secondary);
      return;
    case "top_hat":
      drawTopHat(ctx, r, hat.primary, hat.secondary);
      return;
    case "bandana":
      drawBandana(ctx, r, hat.primary, hat.secondary);
      return;
    case "cat_ears":
      drawCatEars(ctx, r, hat.primary, hat.secondary);
      return;
    case "helmet":
      drawHelmet(ctx, r, hat.primary, hat.secondary);
      return;
    case "headphones":
      drawHeadphones(ctx, r, hat.primary, hat.secondary);
      return;
    case "cape":
      drawCape(ctx, r, hat.primary, hat.secondary, anim.bobTime);
      return;
    case "backpack":
      drawBackpack(ctx, r, hat.primary, hat.secondary);
      return;
    case "tail":
      drawFluffyTail(ctx, r, hat.primary, hat.secondary, anim.bobTime);
      return;
    case "belt":
      drawBelt(ctx, r, hat.primary, hat.secondary);
      return;
    case "pauldrons":
      drawPauldrons(ctx, r, hat.primary, hat.secondary);
      return;
    case "badge":
      drawBadge(ctx, r, hat.primary, hat.secondary);
      return;
    case "shield":
      drawShield(ctx, r, hat.primary, hat.secondary);
      return;
    case "quiver":
      drawQuiver(ctx, r, hat.primary, hat.secondary);
      return;
    case "glasses":
      drawGlasses(ctx, r, hat.primary, hat.secondary);
      return;
    case "cross":
      drawCross(ctx, r, hat.primary, hat.secondary);
      return;
    case "creator_text":
      drawCreatorText(ctx, r);
      return;
    case "custom": {
      // Floats just above the head, sized to read as headwear.
      const side = r * 1.5;
      drawCustomGrid(ctx, hat.cells, hat.grid, r * 0.35 - side / 2, -r * 0.9 - side, side, side);
      return;
    }
  }
}

// "CREATOR" floats above the head, each letter cycling through the hue wheel so
// the word shimmers like a rainbow. Uses performance.now() so it animates even
// in static previews.
function drawCreatorText(ctx: CanvasRenderingContext2D, r: number): void {
  const text = "CREATOR";
  const cx = r * 0.35;
  const cy = -r * 1.45;
  ctx.save();
  ctx.font = "bold 5px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const t = performance.now() / 1000;
  const letters = text.split("");
  const widths = letters.map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0);
  let x = cx - total / 2;
  for (let i = 0; i < letters.length; i += 1) {
    const w = widths[i]!;
    const hue = (t * 120 + i * 42) % 360;
    ctx.fillStyle = `hsl(${hue},90%,55%)`;
    ctx.fillText(letters[i]!, x + w / 2, cy);
    x += w;
  }
  ctx.restore();
}

function drawCross(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  accent: string,
): void {
  const cx = r * 0.35;
  const cy = -r * 1.05;
  ctx.save();
  ctx.shadowColor = accent;
  ctx.shadowBlur = 3;
  ctx.fillStyle = primary;
  ctx.fillRect(cx - 1, cy - 5, 2, 11);
  ctx.fillRect(cx - 4, cy - 2, 8, 2);
  ctx.shadowBlur = 0;
  ctx.fillStyle = accent;
  ctx.fillRect(cx - 0.5, cy - 4, 1, 1);
  ctx.restore();
}

function drawPropeller(
  ctx: CanvasRenderingContext2D,
  r: number,
  hat: Extract<HatRender, { kind: "propeller" }>,
  propAngle: number,
): void {
  const headX = r * 0.35;
  const headY = -r * 0.7;
  // brim
  ctx.fillStyle = "#1a1f2b";
  ctx.beginPath();
  ctx.ellipse(headX, headY + 1, 7, 2, 0, 0, Math.PI * 2);
  ctx.fill();
  // dome wedges
  for (let i = 0; i < 4; i += 1) {
    const a0 = Math.PI + i * (Math.PI / 2);
    ctx.fillStyle = hat.wedges[i]!;
    ctx.beginPath();
    ctx.arc(headX, headY, 6, a0, a0 + Math.PI / 2, false);
    ctx.lineTo(headX, headY);
    ctx.closePath();
    ctx.fill();
  }
  // stalk + button
  ctx.strokeStyle = hat.hub;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(headX, headY - 2);
  ctx.lineTo(headX, headY - 6);
  ctx.stroke();
  ctx.fillStyle = hat.hub;
  ctx.beginPath();
  ctx.arc(headX, headY - 6, 1.2, 0, Math.PI * 2);
  ctx.fill();
  // blade
  ctx.save();
  ctx.translate(headX, headY - 7);
  ctx.rotate(propAngle);
  ctx.fillStyle = hat.blade;
  ctx.fillRect(-7, -0.8, 14, 1.6);
  ctx.restore();
  ctx.fillStyle = hat.hub;
  ctx.beginPath();
  ctx.arc(headX, headY - 7, 1, 0, Math.PI * 2);
  ctx.fill();
}

// --- head positions: top of head ~(r*0.35, -r*0.95) ---

function drawHalo(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(r * 0.35, -r * 1.3, 7, 2.5, 0, 0, Math.PI * 2);
  ctx.stroke();
}

function drawHorns(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(r * 0.15, -r * 0.85);
  ctx.lineTo(r * 0.1, -r * 1.35);
  ctx.lineTo(r * 0.3, -r * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(r * 0.45, -r * 0.85);
  ctx.lineTo(r * 0.55, -r * 1.35);
  ctx.lineTo(r * 0.6, -r * 0.85);
  ctx.closePath();
  ctx.fill();
}

function drawBeret(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(r * 0.35, -r * 0.95, 7, 3, -0.2, 0, Math.PI * 2);
  ctx.fill();
  // stem
  ctx.beginPath();
  ctx.arc(r * 0.6, -r * 1.15, 1.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawHood(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(-r * 0.1, -r * 0.2);
  ctx.quadraticCurveTo(r * 0.1, -r * 1.4, r * 0.55, -r * 1.3);
  ctx.quadraticCurveTo(r * 0.85, -r * 0.7, r * 0.7, -r * 0.2);
  ctx.closePath();
  ctx.fill();
}

function drawVisor(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(r * 0.1, -r * 0.85);
  ctx.lineTo(r * 1.05, -r * 0.55);
  ctx.lineTo(r * 1.05, -r * 0.35);
  ctx.lineTo(r * 0.1, -r * 0.7);
  ctx.closePath();
  ctx.fill();
  // band
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(r * 0.1, -r * 0.85);
  ctx.lineTo(r * 0.6, -r * 1.0);
  ctx.stroke();
}

function drawScarf(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(-r * 0.2, -r * 0.45, r * 1.0, 3);
  // dangle
  ctx.fillRect(r * 0.5, -r * 0.4, 2, 6);
}

function drawBoots(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(-3, r * 0.5, 5, 5);
  ctx.fillRect(-r * 0.55, r * 0.4, 5, 6);
}

function drawGloves(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(r * 0.65, r * 0.35, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.3, r * 0.55, 2.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawMask(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(r * 0.5, -r * 0.3, 6, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();
  // strap
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(r * 0.95, -r * 0.3);
  ctx.lineTo(r * 1.05, -r * 0.45);
  ctx.stroke();
}

function drawEyepatch(ctx: CanvasRenderingContext2D, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(r * 0.5, -r * 0.25, 3, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();
  // strap
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r * 0.2, -r * 0.6);
  ctx.lineTo(r * 0.95, -r * 0.05);
  ctx.stroke();
}

function drawAura(
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  bobTime: number,
): void {
  const pulse = 0.45 + Math.sin(bobTime * 3) * 0.12;
  ctx.save();
  ctx.globalAlpha = pulse;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, r * 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawAuraTrail(
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  bobTime: number,
): void {
  ctx.save();
  ctx.fillStyle = color;
  for (let i = 0; i < 4; i += 1) {
    ctx.globalAlpha = 0.5 - i * 0.1;
    const offset = -i * r * 0.5;
    const wob = Math.sin(bobTime * 4 + i) * 1.5;
    ctx.beginPath();
    ctx.arc(offset, wob, r * (1.2 - i * 0.15), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawWings(
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  bobTime: number,
  flapTime: number,
): void {
  const flap = flapTime > 0 ? 0.4 : 0.2 + Math.sin(bobTime * 6) * 0.15;
  ctx.fillStyle = color;
  ctx.save();
  ctx.translate(0, -r * 0.3);
  ctx.rotate(-flap);
  ctx.beginPath();
  ctx.ellipse(-r * 0.5, 0, r * 0.9, r * 0.35, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.translate(0, r * 0.05);
  ctx.rotate(flap);
  ctx.beginPath();
  ctx.ellipse(-r * 0.5, 0, r * 0.9, r * 0.35, 0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawCrown(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  gem: string,
): void {
  const baseY = -r * 0.85;
  ctx.fillStyle = primary;
  // base band
  ctx.fillRect(r * 0.05, baseY, 11, 2.5);
  // three points
  for (let i = 0; i < 3; i += 1) {
    const px = r * 0.1 + i * 4;
    ctx.beginPath();
    ctx.moveTo(px, baseY);
    ctx.lineTo(px + 2, baseY - 4);
    ctx.lineTo(px + 4, baseY);
    ctx.closePath();
    ctx.fill();
    // gem on each point
    ctx.fillStyle = gem;
    ctx.beginPath();
    ctx.arc(px + 2, baseY - 3.2, 0.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = primary;
  }
}

function drawFedora(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  band: string,
): void {
  const baseY = -r * 0.7;
  // brim
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.ellipse(r * 0.35, baseY, 9, 2, 0, 0, Math.PI * 2);
  ctx.fill();
  // crown
  ctx.fillRect(r * 0.1, baseY - 6, 9, 6);
  // top arc
  ctx.beginPath();
  ctx.ellipse(r * 0.35, baseY - 6, 4.5, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  // band
  ctx.fillStyle = band;
  ctx.fillRect(r * 0.1, baseY - 2, 9, 1.5);
}

function drawTopHat(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  band: string,
): void {
  const baseY = -r * 0.7;
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.ellipse(r * 0.35, baseY, 9, 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(r * 0.13, baseY - 9, 8, 9);
  ctx.beginPath();
  ctx.ellipse(r * 0.35, baseY - 9, 4, 1.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = band;
  ctx.fillRect(r * 0.13, baseY - 2.2, 8, 1.5);
}

function drawBandana(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  dots: string,
): void {
  const baseY = -r * 0.85;
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.moveTo(r * 0.05, baseY);
  ctx.lineTo(r * 0.65, baseY);
  ctx.lineTo(r * 0.55, baseY + 6);
  ctx.lineTo(r * 0.15, baseY + 6);
  ctx.closePath();
  ctx.fill();
  // knot
  ctx.beginPath();
  ctx.arc(r * 0.05, baseY + 3, 1.5, 0, Math.PI * 2);
  ctx.fill();
  // dots
  ctx.fillStyle = dots;
  for (let i = 0; i < 3; i += 1) {
    ctx.beginPath();
    ctx.arc(r * 0.15 + i * 3.5, baseY + 3, 0.7, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCatEars(
  ctx: CanvasRenderingContext2D,
  r: number,
  outer: string,
  inner: string,
): void {
  // left ear
  ctx.fillStyle = outer;
  ctx.beginPath();
  ctx.moveTo(r * 0.1, -r * 0.85);
  ctx.lineTo(r * 0.05, -r * 1.35);
  ctx.lineTo(r * 0.32, -r * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = inner;
  ctx.beginPath();
  ctx.moveTo(r * 0.13, -r * 0.95);
  ctx.lineTo(r * 0.13, -r * 1.2);
  ctx.lineTo(r * 0.25, -r * 0.95);
  ctx.closePath();
  ctx.fill();
  // right ear
  ctx.fillStyle = outer;
  ctx.beginPath();
  ctx.moveTo(r * 0.42, -r * 0.85);
  ctx.lineTo(r * 0.65, -r * 1.35);
  ctx.lineTo(r * 0.6, -r * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = inner;
  ctx.beginPath();
  ctx.moveTo(r * 0.46, -r * 0.95);
  ctx.lineTo(r * 0.58, -r * 1.2);
  ctx.lineTo(r * 0.58, -r * 0.95);
  ctx.closePath();
  ctx.fill();
}

function drawHelmet(
  ctx: CanvasRenderingContext2D,
  r: number,
  body: string,
  stripe: string,
): void {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(r * 0.35, -r * 0.7, 7, Math.PI, 0, false);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = stripe;
  ctx.fillRect(r * 0.32, -r * 1.05, 2, 7);
}

function drawHeadphones(
  ctx: CanvasRenderingContext2D,
  r: number,
  body: string,
  cushion: string,
): void {
  // band
  ctx.strokeStyle = body;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(r * 0.35, -r * 0.7, 7, Math.PI, 0, false);
  ctx.stroke();
  // cushions
  ctx.fillStyle = cushion;
  ctx.beginPath();
  ctx.arc(r * 0.35 - 7, -r * 0.7, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.35 + 7, -r * 0.7, 2.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawCape(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  lining: string,
  bobTime: number,
): void {
  const sway = Math.sin(bobTime * 4) * 2;
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, -r * 0.7);
  ctx.lineTo(-r * 1.6 + sway, -r * 0.6 + sway);
  ctx.lineTo(-r * 1.8 + sway, r * 0.6 + sway);
  ctx.lineTo(-r * 0.5, r * 0.7);
  ctx.closePath();
  ctx.fill();
  // lining stripe
  ctx.fillStyle = lining;
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, -r * 0.7);
  ctx.lineTo(-r * 1.6 + sway, -r * 0.6 + sway);
  ctx.lineTo(-r * 1.55 + sway, -r * 0.45 + sway);
  ctx.lineTo(-r * 0.5, -r * 0.55);
  ctx.closePath();
  ctx.fill();
}

function drawBackpack(
  ctx: CanvasRenderingContext2D,
  r: number,
  body: string,
  strap: string,
): void {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(-r * 0.65, -r * 0.1, 5, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = strap;
  ctx.fillRect(-r * 0.8, -r * 0.35, 7, 1.5);
}

function drawFluffyTail(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  tip: string,
  bobTime: number,
): void {
  const wag = Math.sin(bobTime * 8) * 3;
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.ellipse(-r * 1.5, -r * 0.4 + wag, 6, 3, -0.4, 0, Math.PI * 2);
  ctx.fill();
  // tip
  ctx.fillStyle = tip;
  ctx.beginPath();
  ctx.arc(-r * 1.85, -r * 0.55 + wag, 2.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawBelt(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  buckle: string,
): void {
  ctx.fillStyle = primary;
  ctx.fillRect(-r * 0.8, r * 0.18, r * 1.6, 2.5);
  ctx.fillStyle = buckle;
  ctx.fillRect(-1.5, r * 0.15, 3, 3);
}

function drawPauldrons(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  trim: string,
): void {
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.ellipse(-r * 0.6, -r * 0.4, 4, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(r * 0.05, -r * 0.55, 4, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = trim;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(-r * 0.6, -r * 0.4, 4, 3.5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(r * 0.05, -r * 0.55, 4, 3.5, 0, 0, Math.PI * 2);
  ctx.stroke();
}

function drawBadge(
  ctx: CanvasRenderingContext2D,
  r: number,
  primary: string,
  symbol: string,
): void {
  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.arc(r * 0.05, r * 0.05, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = symbol;
  ctx.beginPath();
  ctx.arc(r * 0.05, r * 0.05, 1.4, 0, Math.PI * 2);
  ctx.fill();
}

function drawShield(
  ctx: CanvasRenderingContext2D,
  r: number,
  body: string,
  trim: string,
): void {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(-r * 0.8, -r * 0.6);
  ctx.lineTo(-r * 0.4, -r * 0.7);
  ctx.lineTo(-r * 0.4, r * 0.55);
  ctx.lineTo(-r * 0.95, 0);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = trim;
  ctx.lineWidth = 1;
  ctx.stroke();
  // boss
  ctx.fillStyle = trim;
  ctx.beginPath();
  ctx.arc(-r * 0.65, -r * 0.05, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawQuiver(
  ctx: CanvasRenderingContext2D,
  r: number,
  body: string,
  arrow: string,
): void {
  ctx.fillStyle = body;
  ctx.fillRect(-r * 0.75, -r * 0.4, 4, 9);
  // arrow fletchings poking out
  ctx.fillStyle = arrow;
  for (let i = 0; i < 3; i += 1) {
    ctx.beginPath();
    ctx.moveTo(-r * 0.7 + i, -r * 0.6);
    ctx.lineTo(-r * 0.6 + i, -r * 0.85);
    ctx.lineTo(-r * 0.5 + i, -r * 0.6);
    ctx.closePath();
    ctx.fill();
  }
}

function drawGlasses(
  ctx: CanvasRenderingContext2D,
  r: number,
  frame: string,
  _lens: string,
): void {
  ctx.strokeStyle = frame;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(r * 0.45, -r * 0.25, 2.4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(r * 0.85, -r * 0.25, 2.4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 0.6, -r * 0.25);
  ctx.lineTo(r * 0.7, -r * 0.25);
  ctx.stroke();
  void _lens;
}
